// src/components/ThemePicker.jsx
// Teal · Ocean · Plum (3.46.0), in Preferences → Appearance. Each card is a
// small picture of the portal in that theme (header, menu, button, table
// heading), drawn in light or dark to match what's showing now. Picking one
// saves it to your account straight away. Until someone picks, they get
// their client's starting theme, marked "Your company's".
import { useAuth } from '../hooks/useAuth';
import { usePreferences } from '../hooks/usePreferences';
import { THEMES, effectiveTheme, effectiveMode, themeLabel } from '../theme/themes';
import { useShownMode } from '../theme/useAppearance';

export default function ThemePicker() {
  const { userInfo } = useAuth();
  const { prefs, setPreference } = usePreferences();
  const mode = useShownMode(effectiveMode(prefs.appearance, userInfo?.clientMode));
  const clientTheme = userInfo?.clientTheme || null;
  const value = effectiveTheme(prefs.theme, clientTheme);
  const index = THEMES.findIndex(t => t.id === value);

  function onKey(e) {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = THEMES[(index + step + THEMES.length) % THEMES.length];
    setPreference('theme', next.id);
    document.getElementById(`pref-theme-${next.id}`)?.focus();
  }

  return (
    <div>
      <style>{CSS}</style>
      <div role="radiogroup" aria-label="Colour theme" className="cp-themes" onKeyDown={onKey}>
        {THEMES.map(t => {
          const p = t.preview[mode];
          const on = value === t.id;
          return (
            <button key={t.id} id={`pref-theme-${t.id}`} type="button" role="radio" aria-checked={on} tabIndex={on ? 0 : -1}
              className={'cp-theme' + (on ? ' on' : '')} onClick={() => setPreference('theme', t.id)}
              style={{ '--ring': p.btn[1], '--ring2': p.btn[2] }}>
              <span className="cp-theme-pic" aria-hidden="true" style={{ background: p.page }}>
                <span className="band" style={{ background: `linear-gradient(110deg, ${p.band[0]}, ${p.band[1]})` }}>
                  <i className="logo" style={{ background: `linear-gradient(135deg, ${p.btn[0]}, ${p.btn[2]})` }} />
                  <b style={{ color: p.onTint }}>Fax Portal</b>
                </span>
                <span className="body">
                  <span className="menu" style={{ background: p.card }}>
                    <i style={{ background: p.tint }} /><i style={{ background: p.muted, opacity: 0.35 }} /><i style={{ background: p.muted, opacity: 0.35 }} />
                  </span>
                  <span className="card" style={{ background: p.card }}>
                    <i className="head" style={{ background: `linear-gradient(110deg, ${p.head[0]}, ${p.head[1]})` }} />
                    <i className="line" style={{ background: p.text, opacity: 0.55 }} />
                    <i className="line short" style={{ background: p.muted, opacity: 0.45 }} />
                    <i className="btn" style={{ background: `linear-gradient(120deg, ${p.btn[0]}, ${p.btn[1]} 55%, ${p.btn[2]})` }} />
                  </span>
                </span>
              </span>
              <span className="cp-theme-name">
                {t.label}
                {on && <span className="tick" aria-hidden="true">✓</span>}
                {clientTheme === t.id && <span className="client">Your company's</span>}
              </span>
              <span className="cp-theme-blurb">{t.blurb}</span>
            </button>
          );
        })}
      </div>
      {prefs.theme && clientTheme && prefs.theme !== clientTheme && (
        <button type="button" className="cp-theme-reset" onClick={() => setPreference('theme', '')}>
          Use my company's theme ({themeLabel(clientTheme)})
        </button>
      )}
    </div>
  );
}

const CSS = `
.cp-themes { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 12px; margin-top: 4px; }
.cp-theme { display: grid; gap: 6px; text-align: left; padding: 8px 8px 10px; border-radius: 14px; cursor: pointer; font: inherit;
  background: var(--c-ffffff); border: 1.5px solid var(--c-cfd8dc); color: var(--t-263238);
  transition: transform .2s cubic-bezier(.34,1.5,.64,1), box-shadow .2s ease, border-color .2s ease; }
/* 3.49.19: grows in place (moving up made the box slip from under the pointer at its edge and flicker, like the cards in 3.48.6) */
.cp-theme:hover { transform: scale(1.025); box-shadow: 0 10px 22px rgb(var(--c-000000-rgb) / 0.14); }
.cp-theme:focus-visible { outline: 2px solid var(--c-00897b); outline-offset: 3px; }
.cp-theme.on { border-color: transparent; box-shadow: 0 0 0 2.5px var(--ring), 0 10px 24px rgb(var(--c-000000-rgb) / 0.16); }
.cp-theme-pic { display: grid; grid-template-rows: 22px 1fr; height: 92px; border-radius: 9px; overflow: hidden; border: 1px solid rgb(var(--c-000000-rgb) / 0.08); }
.cp-theme-pic .band { display: flex; align-items: center; gap: 5px; padding: 0 7px; position: relative; overflow: hidden; }
.cp-theme-pic .band::after { content: ''; position: absolute; top: 0; bottom: 0; width: 40%; left: -60%; background: linear-gradient(100deg, transparent, rgb(255 255 255 / 0.45), transparent); transform: skewX(-18deg); }
.cp-theme:hover .cp-theme-pic .band::after, .cp-theme.on .cp-theme-pic .band::after { animation: cpThemeShine 2.6s ease-in-out infinite; }
.cp-theme-pic .logo { width: 10px; height: 10px; border-radius: 3px; }
.cp-theme-pic b { font-size: 9.5px; font-weight: 800; letter-spacing: -0.2px; }
.cp-theme-pic .body { display: grid; grid-template-columns: 30% 1fr; gap: 5px; padding: 6px; }
.cp-theme-pic .menu, .cp-theme-pic .card { border-radius: 5px; padding: 4px; display: grid; gap: 4px; align-content: start; }
.cp-theme-pic i { display: block; font-style: normal; }
.cp-theme-pic .menu i { height: 7px; border-radius: 3px; }
.cp-theme-pic .head { height: 8px; border-radius: 3px; }
.cp-theme-pic .line { height: 4px; width: 90%; border-radius: 2px; }
.cp-theme-pic .line.short { width: 60%; }
.cp-theme-pic .btn { height: 10px; width: 46%; border-radius: 99px; margin-top: 2px; }
.cp-theme-name { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font-weight: 800; font-size: 14px; padding: 0 2px; }
.cp-theme-name .tick { display: inline-grid; place-items: center; width: 18px; height: 18px; border-radius: 50%; color: #fff; font-size: 11px;
  background: linear-gradient(135deg, var(--ring), var(--ring2)); animation: cpThemePop .35s cubic-bezier(.3,1.8,.5,1); }
.cp-theme-name .client { font-size: 10.5px; font-weight: 700; padding: 1px 7px; border-radius: 99px; background: var(--c-eceff1); color: var(--t-546e7a); }
.cp-theme-blurb { font-size: 12px; color: var(--t-546e7a); padding: 0 2px; line-height: 1.35; }
.cp-theme-reset { margin-top: 10px; background: none; border: none; padding: 0; font: inherit; font-size: 13px; font-weight: 600; color: var(--t-00897b); text-decoration: underline; cursor: pointer; }
@keyframes cpThemeShine { 0% { left: -60%; } 60%, 100% { left: 130%; } }
@keyframes cpThemePop { from { transform: scale(0); } to { transform: none; } }
`;
